/**
 * All localStorage keys should be defined here to prevent collisions.
 *
 * NOTE: the auth JWT (AUTH_TOKEN) is persisted in localStorage by design. For
 * this client-only SPA we accept the tradeoff that a successful XSS could read
 * the token, in exchange for simple cross-tab persistence without a cookie/CSRF
 * setup. Keep this in mind before widening what can read it (e.g. error
 * reporters that capture URLs/state) or rendering any untrusted HTML.
 *
 * The token is the single source of truth for being signed in. User identity
 * and settings are NOT persisted here; they are server state fetched via the
 * ["user"] query (GET /dashboard/user).
 */
export const STORAGE_KEYS = {
  SELECTED_SERVICE_ID: "appio_selected_service_id",
  AUTH_TOKEN: "appio_auth_token",
  LAST_LOGIN_METHOD: "appio_last_login_method",
} as const;

// Legacy key, no longer written. Cleared on sign-out so stale identity from a
// previous build doesn't linger in localStorage.
const LEGACY_AUTH_USER_KEY = "appio_auth_user";

/**
 * Shown as a "Last used" hint on the login page, so it intentionally survives
 * sign-out (useLogout snapshots and restores it around localStorage.clear()).
 */
export type LastLoginMethod = "email" | "google";

/**
 * Same-tab notification that the auth token changed. The native `storage` event
 * only fires in OTHER tabs, so token writes in this tab also dispatch this
 * custom event.
 */
const AUTH_TOKEN_CHANGED_EVENT = "appio:auth-token-changed";

function emitAuthTokenChange(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED_EVENT));
  }
}

/**
 * Set when a live session is ended FOR the user rather than BY them (the
 * expiry watchdog in auth-store, or the interceptor meeting a rejected token),
 * so /login can say why they are back there.
 *
 * sessionStorage, for two reasons: it survives the interceptor's full-page
 * redirect (unlike React state or a router location), and it is scoped to the
 * tab where the session actually died (unlike localStorage, which would put the
 * notice in front of every other tab too).
 */
const SESSION_EXPIRED_KEY = "appio_session_expired";

function writeSessionExpired(expired: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (expired) {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
    } else {
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }
  } catch {
    // sessionStorage unavailable (private mode quota, blocked site data). The
    // notice is a courtesy, so losing it must never break signing in.
  }
}

export const storage = {
  getSelectedServiceId(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.SELECTED_SERVICE_ID);
  },

  setSelectedServiceId(serviceId: string): void {
    localStorage.setItem(STORAGE_KEYS.SELECTED_SERVICE_ID, serviceId);
  },

  removeSelectedServiceId(): void {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_SERVICE_ID);
  },

  getAuthToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  setAuthToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    writeSessionExpired(false);
    emitAuthTokenChange();
  },

  clearAuthSession(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(LEGACY_AUTH_USER_KEY);
    emitAuthTokenChange();
  },

  /**
   * Call BEFORE clearing the token: clearing notifies the auth store
   * synchronously, which is what routes to /login.
   */
  markSessionExpired(): void {
    writeSessionExpired(true);
  },

  /** Also retired by useLogout's sessionStorage.clear(). */
  wasSessionExpired(): boolean {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1";
    } catch {
      return false;
    }
  },

  /** `callback` should re-read getAuthToken(); it is not handed the new value. */
  onAuthTokenChange(callback: () => void): () => void {
    if (typeof window === "undefined") return () => {};
    const onCustom = () => callback();
    const onStorage = (event: StorageEvent) => {
      // key === null means localStorage.clear() (e.g. a cross-tab logout).
      if (event.key === null || event.key === STORAGE_KEYS.AUTH_TOKEN) {
        callback();
      }
    };
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  },

  getLastLoginMethod(): LastLoginMethod | null {
    if (typeof window === "undefined") return null;
    const value = localStorage.getItem(STORAGE_KEYS.LAST_LOGIN_METHOD);
    return value === "email" || value === "google" ? value : null;
  },

  setLastLoginMethod(method: LastLoginMethod): void {
    localStorage.setItem(STORAGE_KEYS.LAST_LOGIN_METHOD, method);
  },
};
